import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  InputAdornment
} from "@mui/material";

import { ArrowBack as ArrowBackIcon, Clear as ClearIcon } from "@mui/icons-material";
import AuthApi4 from "../services/authApi";
import Navbar from "../sidebar/SideNavbar";
import Footer from "../sidebar/Footer";
import Swal from "sweetalert2";

// 🔥 SEARCH DEBOUNCE DELAY
const SEARCH_DELAY = 450;

export default function BulkAddChannel() {
  const location = useLocation();
  const { stbId } = location.state || {};

  const [activeSection, setActiveSection] = useState("bouquetChannel");
  const navigate = useNavigate();

  const [section1Items, setSection1Items] = useState([]);
  const [section2Items, setSection2Items] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]); // ALWAYS subs_id only

  const [loading, setLoading] = useState(false);
  const [planD, setPlanD] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    addChannel("bouquetChannel");
  }, []);

  const addChannel = async (section) => {
    if (section === "bouquetChannel") bouquetChannelDetails();
    else payChannelDetails();
  };

  const payChannelDetails = async () => {
    let data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          CUST_ID: localStorage.getItem("payment_cust_num"),
          genre: "C",
          type: "bulkAddChnl"
        }
      ]
    };

    await AuthApi4.addChannels(data)
      .then((response) => setSection1Items(response.data.packDetails))
      .catch(() => console.log("Error Pay Packs"));
  };

  const bouquetChannelDetails = async () => {
    let data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          CUST_ID: localStorage.getItem("payment_cust_num"),
          genre: "A",
          type: "bulkAddChnl"
        }
      ]
    };

    await AuthApi4.addChannels(data)
      .then((response) => setSection2Items(response.data.packDetails))
      .catch(() => console.log("Error Bouquet Packs"));
  };

  const handleCheckboxChange = (subs_id) => {
    setSelectedItems((prev) =>
      prev.includes(subs_id)
        ? prev.filter((id) => id !== subs_id)
        : [...prev, subs_id]
    );
  };

  useEffect(() => {
    packTableInfo();
  }, [selectedItems]);

  const packTableInfo = async () => {
    if (selectedItems.length === 0) {
      setPlanD([]);
      setTotalPrice(0);
      return;
    }

    let data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          subs_id: selectedItems,
          type: "packInfo"
        }
      ]
    };

    await AuthApi4.addChannels(data)
      .then((response) => {
        setPlanD(response.data.packDetails);

        const total = response.data.packDetails.reduce(
          (acc, item) => acc + parseFloat(item.subs_grnd_tot_prc || 0),
          0
        );

        setTotalPrice(total);
      })
      .catch(() => console.log("Error Pack Info"));
  };

  // 🔥 LIVE SEARCH WITH CLEAR ICON
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm === "") {
        addChannel(activeSection);
        return;
      }
      handleSearch();
    }, SEARCH_DELAY);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handleSearch = async () => {
    let data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          stbId: stbId,
          genre: activeSection === "bouquetChannel" ? "A" : "C",
          subs_name: searchTerm,
          type: "packFilter"
        }
      ]
    };

    await AuthApi4.addChannels(data)
      .then((response) => {
        if (activeSection === "bouquetChannel")
          setSection2Items(response.data.packDetails);
        else setSection1Items(response.data.packDetails);
      })
      .catch(() => console.log("Search Error"));
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    addChannel(activeSection);
  };

  const handleAddChannelBtn = async () => {
    if (selectedItems.length === 0) {
      Swal.fire({
        icon: "error",
        title: "No Packs Selected"
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to Add Channels?",
      icon: "warning",
      showCancelButton: true
    });

    if (!result.isConfirmed) return;

    setLoading(true);

    let dataWallet = {
      records: [{ CUST_ID: localStorage.getItem("payment_cust_num"), type: "wallet" }]
    };

    await AuthApi4.stbinfo(dataWallet).then(async (res) => {
      const wallet = parseFloat(res.data.stbdetails[0].bill_total);
      const required = totalPrice * stbId.length;

      if (wallet < required) {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Insufficient Balance"
        });
        return;
      }

      let finalData = {
        operator_id: parseInt(localStorage.getItem("ownerId")),
        CUST_ID: localStorage.getItem("payment_cust_num"),
        stb_ids: stbId,
        subs_ids: selectedItems.map((id) => ({ subs_id: id })),
        type: "bulkaddChnls"
      };

      await AuthApi4.bulkSTBRenew(finalData)
        .then((response) => {
          setLoading(false);
          Swal.fire({
            icon: "success",
            title: "Added Successfully"
          }).then(() => navigate("/bulkRenewal"));
        })
        .catch(() => {
          setLoading(false);
          Swal.fire({ icon: "error", title: "Failed to Add" });
        });
    });
  };

  return (
    <div style={{ display: "flex", background: "#F5F5F5" }}>
      <CssBaseline />
      <Navbar />

      <main style={{ flexGrow: 1, padding: "5px", minHeight: "100vh" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: 10 }}>
          <Typography variant="h5">Bulk Add Channels</Typography>
          <IconButton
            onClick={() => navigate("/bulkRenewal")}
            sx={{ background: "#ff8c00", color: "#fff" }}
          >
            <ArrowBackIcon /> Back
          </IconButton>
        </Box>

        <Container sx={{ maxWidth: "lg" }}>
          <Paper sx={{ padding: 2 }}>
            {/* SECTION SWITCH */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: "pointer",
                    background: activeSection === "bouquetChannel" ? "#ff8c00" : "#fff",
                    color: activeSection === "bouquetChannel" ? "#fff" : "#000"
                  }}
                  onClick={() => {
                    setActiveSection("bouquetChannel");
                    addChannel("bouquetChannel");
                  }}
                >
                  <CardContent>
                    <Typography>Bouquet Packs</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card
                  sx={{
                    cursor: "pointer",
                    background: activeSection === "payChannel" ? "#ff8c00" : "#fff",
                    color: activeSection === "payChannel" ? "#fff" : "#000"
                  }}
                  onClick={() => {
                    setActiveSection("payChannel");
                    addChannel("payChannel");
                  }}
                >
                  <CardContent>
                    <Typography>Pay Channels</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* SEARCH FIELD WITH CLEAR ICON */}
            <Box sx={{ marginTop: 2, width: "550px" }}>
              <TextField
                fullWidth
                label="Search pack name..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* CHECKBOX LIST + PRICE TABLE */}
            <Box sx={{ marginTop: 3 }}>
              <Grid container spacing={2}>
                {/* PACK LIST */}
                <Grid item xs={6}>
                  <Box
                    sx={{
                      height: "220px",
                      border: "1px solid black",
                      overflowY: "auto"
                    }}
                  >
                    <FormControl>
                      <FormGroup>
                        {(activeSection === "payChannel"
                          ? section1Items
                          : section2Items
                        ).map((item) => (
                          <FormControlLabel
                            key={item.subs_id}
                            control={
                              <Checkbox
                                checked={selectedItems.includes(item.subs_id)}
                                onChange={() => handleCheckboxChange(item.subs_id)}
                              />
                            }
                            label={item.sub_name}
                          />
                        ))}
                      </FormGroup>
                    </FormControl>
                  </Box>
                </Grid>

                {/* PRICE TABLE */}
                <Grid item xs={6}>
                  <TableContainer
                    component={Paper}
                    sx={{ height: "220px", overflowY: "auto" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>SI</TableCell>
                          <TableCell>Prod Id</TableCell>
                          <TableCell>Pack Name</TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {planD.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No data
                            </TableCell>
                          </TableRow>
                        ) : (
                          planD.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>{row.prod_id}</TableCell>
                              <TableCell>{row.subs_desc}</TableCell>
                              <TableCell>{row.subs_grnd_tot_prc}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>

            {/* TOTAL + BUTTONS */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 2,
                borderTop: "1px solid #ddd",
                paddingTop: 2
              }}
            >
              {/* Total Price */}
              <Typography variant="h6">Total: ₹{totalPrice.toFixed(2)}</Typography>

              {/* Buttons */}
              <Box>
                <Button sx={{ marginRight: 2 }} onClick={() => window.location.reload()}>
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  sx={{ background: "#ff8c00", color: "#fff" }}
                  onClick={handleAddChannelBtn}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={28} /> : "Add"}
                </Button>
              </Box>
            </Box>

          </Paper>
        </Container>

        <Footer />
      </main>
    </div>
  );
}
